/**
 * Shared styling constants for severity, issue status, and comment types.
 *
 * Single source of truth for mapping levels to Tailwind classes.
 * Import from here instead of defining inline Record maps per component.
 */

/** Text color for severity labels and inline text */
export const SEVERITY_TEXT: Record<string, string> = {
  critical: "text-red-600 dark:text-red-400",
  high: "text-orange-600 dark:text-orange-400",
  medium: "text-yellow-600 dark:text-yellow-400",
  low: "text-blue-600 dark:text-blue-400",
  info: "text-gray-600 dark:text-gray-400",
};

/** Background + border for severity-aware containers (cards, alerts) */
export const SEVERITY_BG: Record<string, string> = {
  critical: "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900",
  high: "bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-900",
  medium: "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-900",
  low: "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900",
  info: "bg-gray-50 border-gray-200 dark:bg-gray-950/30 dark:border-gray-800",
};

/** Badge styling (background + text + border) for severity badges */
export const SEVERITY_BADGE: Record<string, string> = {
  critical:
    "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700",
  high: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-700",
  medium:
    "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-300 dark:border-yellow-700",
  low: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700",
  info: "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600",
};

/** Dot/indicator background colors for compact severity indicators */
export const SEVERITY_DOT: Record<string, string> = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-yellow-500",
  low: "bg-blue-500",
  info: "bg-gray-400",
};

/** Left accent border for cards and list items */
export const SEVERITY_BORDER_LEFT: Record<string, string> = {
  critical: "border-l-red-500",
  high: "border-l-orange-500",
  medium: "border-l-yellow-500",
  low: "border-l-blue-500",
  info: "border-l-gray-400",
};

// ── Issue Status ────────────────────────────────────────────────────────

/** Human-readable labels for issue status values */
export const STATUS_LABELS: Record<string, string> = {
  OPEN: "Open",
  IN_REVIEW: "In Review",
  RESOLVED: "Resolved",
  FALSE_POSITIVE: "Closed",
};

/** Dot/indicator background colors for issue status */
export const STATUS_DOT: Record<string, string> = {
  OPEN: "bg-blue-500",
  IN_REVIEW: "bg-amber-500",
  RESOLVED: "bg-emerald-500",
  FALSE_POSITIVE: "bg-muted-foreground/50",
};

// ── Comment Types ───────────────────────────────────────────────────────

import type { CommentType } from "@/types/issueComment";

/** Human-readable labels for comment types */
export const COMMENT_TYPE_LABELS: Record<CommentType, string> = {
  GENERAL: "General",
  INVESTIGATION_NOTE: "Investigation Note",
  DECISION_RATIONALE: "Decision Rationale",
  FALSE_POSITIVE_JUSTIFICATION: "False Positive Justification",
};

/** Badge styling for comment type badges */
export const COMMENT_TYPE_COLORS: Record<CommentType, string> = {
  GENERAL: "bg-gray-100 text-gray-700 border-gray-300",
  INVESTIGATION_NOTE: "bg-blue-100 text-blue-700 border-blue-300",
  DECISION_RATIONALE: "bg-purple-100 text-purple-700 border-purple-300",
  FALSE_POSITIVE_JUSTIFICATION: "bg-yellow-100 text-yellow-700 border-yellow-300",
};
